import { createContext, useContext, useMemo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { translations, Locale, TranslationKey } from "./translations";

interface LocaleContextValue {
  locale: Locale;
  t: (key: TranslationKey) => string;
  localePath: (path: string) => string;
  switchLocalePath: (target?: Locale) => string;
}

const ROUTE_MAP_EN: Record<string, string> = {
  "/": "/en",
  "/sobre-nosotros": "/en/about-us",
  "/espacio-informativo": "/en/resources",
  "/aviso-legal": "/en/legal-notice",
  "/politica-privacidad": "/en/privacy-policy",
  "/politica-cookies": "/en/cookie-policy",
  "/cita-confirmada": "/en/booking-confirmed",
};

const ROUTE_MAP_FR: Record<string, string> = {
  "/": "/fr",
  "/sobre-nosotros": "/fr/a-propos",
  "/espacio-informativo": "/fr/ressources",
  "/aviso-legal": "/fr/mentions-legales",
  "/politica-privacidad": "/fr/politique-confidentialite",
  "/politica-cookies": "/fr/politique-cookies",
  "/cita-confirmada": "/fr/rendez-vous-confirme",
};

const REVERSE_EN = Object.fromEntries(
  Object.entries(ROUTE_MAP_EN).map(([es, en]) => [en, es])
);

const REVERSE_FR = Object.fromEntries(
  Object.entries(ROUTE_MAP_FR).map(([es, fr]) => [fr, es])
);

const getLocaleFromPath = (pathname: string): Locale =>
  pathname.startsWith("/en") ? "en" : pathname.startsWith("/fr") ? "fr" : "es";

const toCanonicalEs = (path: string): string => {
  if (REVERSE_EN[path]) return REVERSE_EN[path];
  if (REVERSE_FR[path]) return REVERSE_FR[path];

  const enBlog = path.match(/^\/en\/resources\/(.+)$/);
  if (enBlog) return `/espacio-informativo/${enBlog[1]}`;

  const frBlog = path.match(/^\/fr\/ressources\/(.+)$/);
  if (frBlog) return `/espacio-informativo/${frBlog[1]}`;

  return path;
};

const createLocaleValue = (locale: Locale, pathname: string): LocaleContextValue => {
  const t = (key: TranslationKey): string => {
    return translations[locale][key] ?? translations.es[key] ?? key;
  };

  const localePath = (path: string): string => {
    if (locale === "es") return path;

    if (locale === "en") {
      if (ROUTE_MAP_EN[path]) return ROUTE_MAP_EN[path];
      return path === "/" ? "/en" : `/en${path}`;
    }

    if (ROUTE_MAP_FR[path]) return ROUTE_MAP_FR[path];
    return path === "/" ? "/fr" : `/fr${path}`;
  };

  const switchLocalePath = (target?: Locale): string => {
    const canonical = toCanonicalEs(pathname);
    const next: Locale =
      target ?? (locale === "es" ? "en" : locale === "en" ? "fr" : "es");

    if (next === "es") return canonical;

    if (next === "en") {
      if (ROUTE_MAP_EN[canonical]) return ROUTE_MAP_EN[canonical];
      const blog = canonical.match(/^\/espacio-informativo\/(.+)$/);
      if (blog) return `/en/resources/${blog[1]}`;
      return "/en";
    }

    if (ROUTE_MAP_FR[canonical]) return ROUTE_MAP_FR[canonical];
    const blog = canonical.match(/^\/espacio-informativo\/(.+)$/);
    if (blog) return `/fr/ressources/${blog[1]}`;
    return "/fr";
  };

  return { locale, t, localePath, switchLocalePath };
};

const getFallbackLocaleValue = (): LocaleContextValue => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  return createLocaleValue(getLocaleFromPath(pathname), pathname);
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const locale = getLocaleFromPath(location.pathname);
  const value = useMemo(
    () => createLocaleValue(locale, location.pathname),
    [locale, location.pathname]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  return ctx ?? getFallbackLocaleValue();
};
